import * as React from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { SpotlightCard, SpotlightCardContent } from "./SpotlightCard";
import { GradientText, SectionLabel } from "./GradientText";
import { Check } from "lucide-react";

interface PricingTier {
  id: string;
  name?: string;
  description?: string;
  price: {
    amount: number | null;
    currency: string;
    period: string;
    displayText?: string;
  };
  features?: string[];
  cta: { text?: string };
  highlighted?: boolean;
  badge?: string;
}

interface PricingProps {
  tiers: PricingTier[];
}

/**
 * Pricing
 *
 * Pricing table with:
 * - Highlighted "popular" tier with accent border
 * - Feature checklist
 * - Responsive grid layout
 * - CTA buttons with appropriate styles
 * - Text from translations keyed by tier id
 */
export function Pricing({ tiers }: PricingProps) {
  const { t } = useTranslation("landing");
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  const title = t("pricing.title");
  const subtitle = t("pricing.subtitle");
  const billingNote = t("pricing.billingNote");

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="pricing" className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "mx-auto mb-16 max-w-3xl text-center lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
          )}
        >
          <SectionLabel className="mb-6">Pricing</SectionLabel>

          <h2 className="mb-4 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
            <GradientText>{title}</GradientText>
          </h2>

          {subtitle && (
            <p className="mb-4 text-base text-muted-foreground lg:text-lg">{subtitle}</p>
          )}

          {billingNote && <p className="text-sm text-muted-foreground">{billingNote}</p>}
        </div>

        {/* Pricing tiers */}
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {tiers.map((tier, index) => {
            const tierName = t(`pricing.${tier.id}.name`, { defaultValue: tier.name });
            const tierDescription = t(`pricing.${tier.id}.description`, {
              defaultValue: tier.description,
            });
            const tierCta = t(`pricing.${tier.id}.cta`, { defaultValue: tier.cta?.text });
            const tierFeatures = t(`pricing.${tier.id}.features`, {
              returnObjects: true,
              defaultValue: tier.features,
            }) as string[];
            const tierBadge = t(`pricing.${tier.id}.badge`, {
              defaultValue: tier.badge || "",
            });

            return (
              <SpotlightCard
                key={tier.id}
                className={cn(
                  "relative",
                  "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                  isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0",
                  tier.highlighted && "border-primary",
                )}
                style={{
                  transitionDelay: isVisible ? `${index * 100}ms` : "0ms",
                }}
              >
                {/* Popular badge */}
                {tierBadge && (
                  <div
                    className={cn(
                      "absolute -top-3 left-1/2 -translate-x-1/2",
                      "rounded-full px-3 py-1",
                      "bg-primary text-xs font-medium text-primary-foreground",
                    )}
                  >
                    {tierBadge}
                  </div>
                )}

                <SpotlightCardContent className="p-6 lg:p-8">
                  {/* Tier header */}
                  <div className="mb-6">
                    <h3 className="mb-2 text-xl font-semibold text-foreground">{tierName}</h3>
                    <p className="text-sm text-muted-foreground">{tierDescription}</p>
                  </div>

                  {/* Price */}
                  <div className="mb-6">
                    {tier.price.amount !== null ? (
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-semibold text-foreground lg:text-5xl">
                          ${tier.price.amount}
                        </span>
                        <span className="text-sm text-muted-foreground">/{tier.price.period}</span>
                      </div>
                    ) : (
                      <div className="text-4xl font-semibold text-foreground lg:text-5xl">
                        {tier.price.displayText || "Custom"}
                      </div>
                    )}
                  </div>

                  {/* CTA Button */}
                  <Link to="/signup" className="mb-6 block w-full">
                    <Button
                      className={cn(
                        "w-full",
                        !tier.highlighted && [
                          "bg-muted hover:bg-muted/80",
                          "text-foreground",
                          "border border-border",
                        ],
                      )}
                    >
                      {tierCta}
                    </Button>
                  </Link>

                  {/* Features list */}
                  <ul className="space-y-3">
                    {Array.isArray(tierFeatures) &&
                      tierFeatures.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm">
                          <Check
                            className={cn(
                              "mt-0.5 h-4 w-4 flex-shrink-0",
                              tier.highlighted ? "text-primary" : "text-muted-foreground",
                            )}
                          />
                          <span className="text-muted-foreground">{feature}</span>
                        </li>
                      ))}
                  </ul>
                </SpotlightCardContent>
              </SpotlightCard>
            );
          })}
        </div>
      </div>
    </section>
  );
}
